import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { MessageSquarePlus, X, Bug, Lightbulb, MessageCircle, Send } from "lucide-react";

const TYPES = [
  { key: "bug", label: "Bug Report", icon: Bug },
  { key: "feature", label: "Feature Request", icon: Lightbulb },
  { key: "general", label: "General", icon: MessageCircle },
];

export default function FeedbackWidget() {
  const { user } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [type, setType] = useState("bug");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const openHandler = () => setOpen(true);
    window.addEventListener("cbr:open-feedback", openHandler);
    return () => window.removeEventListener("cbr:open-feedback", openHandler);
  }, []);

  // Suppress the beta feedback widget on Marketplace / order routes so it never
  // covers product or checkout controls (esp. on mobile). Unchanged elsewhere.
  const suppressed =
    /^\/marketplace(\/|$)/.test(location.pathname) ||
    /^\/account\/orders(\/|$)/.test(location.pathname);
  useEffect(() => { if (suppressed && open) setOpen(false); }, [suppressed, open]);
  if (suppressed) return null;

  const submit = async () => {
    if (message.trim().length < 2) { toast.error("Please add a short description"); return; }
    setBusy(true);
    try {
      await api.post("/feedback", {
        type,
        message: message.trim(),
        context: {
          url: window.location.href,
          page: location.pathname,
          browser: navigator.userAgent,
          screen: `${window.innerWidth}x${window.innerHeight}`,
          timestamp: new Date().toISOString(),
        },
      });
      toast.success("Thanks! Your feedback was sent.");
      setMessage(""); setOpen(false);
    } catch {
      toast.error("Couldn't send feedback — please try again.");
    } finally { setBusy(false); }
  };

  return (
    <>
      <button data-testid="feedback-trigger" onClick={() => setOpen(true)} aria-label="Send beta feedback"
        className="fixed bottom-4 left-4 z-[90] inline-flex items-center gap-2 bg-[#0A192F] text-white pl-3 pr-4 py-2.5 rounded-full shadow-lg hover:bg-[#E63946] transition-colors">
        <MessageSquarePlus className="w-4 h-4" />
        <span className="text-xs font-semibold uppercase tracking-wider">Feedback</span>
      </button>

      {open && (
        <div className="fixed inset-0 z-[110] flex items-end sm:items-center justify-center sm:justify-start p-4 sm:pl-6" data-testid="feedback-modal">
          <div className="absolute inset-0 bg-[#0A192F]/50 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <div className="relative w-full max-w-sm bg-white rounded-lg shadow-2xl border border-slate-200 sm:mb-16">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
              <div className="font-display text-lg uppercase tracking-tight text-[#0A192F]">Beta Feedback</div>
              <button data-testid="feedback-close" onClick={() => setOpen(false)} className="text-slate-400 hover:text-[#0A192F]"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-4 space-y-3">
              <div className="grid grid-cols-3 gap-2">
                {TYPES.map((t) => (
                  <button key={t.key} data-testid={`feedback-type-${t.key}`} onClick={() => setType(t.key)}
                    className={`flex flex-col items-center gap-1 py-2.5 rounded-sm border text-[11px] font-semibold uppercase tracking-wider transition-colors ${type === t.key ? "border-[#E63946] bg-[#E63946]/5 text-[#E63946]" : "border-slate-200 text-slate-500 hover:border-slate-300"}`}>
                    <t.icon className="w-4 h-4" /> {t.label}
                  </button>
                ))}
              </div>
              <textarea data-testid="feedback-message" value={message} onChange={(e) => setMessage(e.target.value)} rows={4}
                placeholder={type === "bug" ? "What happened? What did you expect?" : type === "feature" ? "What would you like to see?" : "Share your thoughts…"}
                className="w-full border border-slate-200 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#E63946]" />
              <p className="text-[10px] text-slate-400">We automatically include the current page, your role, browser and screen size to help us reproduce issues.</p>
              <button data-testid="feedback-submit" onClick={submit} disabled={busy}
                className="w-full inline-flex items-center justify-center gap-2 bg-[#E63946] text-white py-2.5 rounded-sm text-sm font-semibold uppercase tracking-wider hover:bg-[#c62f3b] disabled:opacity-60">
                <Send className="w-4 h-4" /> {busy ? "Sending…" : "Send Feedback"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
